import { useState } from "react";
import {
  TextField,
  Typography,
  Box,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "../../hooks/useForm";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "../Button/LoadingButton";
import { AuthAPI } from "../../services/api/auth";
import { SignupRequest } from "../../types/RoleType";
import { useMutation } from "@tanstack/react-query";
import { usersApi } from "../../services/api/user";
import { ROUTES } from "../../router/routes";

const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const navigate = useNavigate();
  const { values, handleChange, errors, isLoading, handleSubmit, setErrors } =
    useForm({
      initialValues: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmpassword: "",
        instituteName: "",
        phonenumber: "",
      },
      validate: (values) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
          errors.firstName = "firstName is required";
        }
        if (!values.password) {
          errors.password = "Password is required";
        }
        if (!values.phonenumber) {
          errors.phonenumber = "phonenumber is required";
        } else if (!/^\d{10}$/.test(values.phonenumber)) {
          errors.phonenumber = "Phone number must be 10 digits";
        }
        if (values.password.length < 6) {
          errors.password = "Password must be at least 6 characters";
        }
        if (values.password !== values.confirmpassword) {
          errors.confirmpassword = "Passwords do not match";
        }
        if (!values.instituteName) {
          errors.instituteName = "instituteName is required";
        }
        if (!values.email) {
          errors.email = "email is required";
        }
        return errors;
      },
      onSubmit: async (values) => {
        if (!agreedToTerms) {
          setTermsError(true);
          return;
        }
        try {
          const request: SignupRequest = {
            email: values.email,
            firstname: values.firstName,
            instituteName: values.instituteName,
            lastname: values.lastName,
            password: values.password,
            userType: "Admin",
            roles: ["InstituteAdmin"],
            phonenumber: values.phonenumber,
          };
          await AuthAPI.signup(request);
          toast.success("signup successful!");
          navigate("/login");
        } catch (error: unknown) {
          let message: string = "Signup failed. Please try again.";
          if (error && typeof error === "object" && "message" in error) {
            message = error.message as string;
          }
          toast.error(message);
          console.log("Login error:", error);
        }
      },
    });

  const checkDuplicateMutation = useMutation({
    mutationFn: ({
      userName,
      type,
    }: {
      userName: string;
      type: "phone" | "email";
    }) =>
      usersApi.checkDuplicate(
        type === "email" ? userName : undefined,
        type === "phone" ? userName : undefined
      ),
    onSuccess: (data, variables) => {
      const fieldName = variables.type === "email" ? "email" : "phonenumber";

      if (!data.data) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: `${
            variables.type === "phone" ? "Phone Number" : "Email"
          } already exists`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to check duplicate");
    },
  });
  const handleBlur = async (check: "phone" | "email") => {
    if (check == "phone" && values.phonenumber) {
      await checkDuplicateMutation.mutateAsync({
        userName: values.phonenumber,
        type: "phone",
      });
    } else if (check == "email" && values.email) {
      await checkDuplicateMutation.mutateAsync({
        userName: values.email,
        type: "email",
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", mt: 2 }}>
      <Typography
        variant="h5"
        component="h1"
        sx={{ mb: 3, textAlign: "center" }}
      >
        Create Your SkolarMinds Account
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={values.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={values.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Grid>
      </Grid>

      <TextField
        margin="normal"
        required
        fullWidth
        id="phonenumber"
        label="mobile number"
        name="phonenumber"
        autoComplete="phonenumber"
        value={values.phonenumber}
        onChange={handleChange}
        error={!!errors.phonenumber}
        helperText={errors.phonenumber}
        onBlur={() => handleBlur("phone")}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        id="instituteName"
        label="Institute Name"
        name="instituteName"
        value={values.instituteName}
        onChange={handleChange}
        error={!!errors.instituteName}
        helperText={errors.instituteName}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        inputProps={{
          type: "email",
        }}
        value={values.email}
        onChange={handleChange}
        error={!!errors.email}
        helperText={errors.email}
        onBlur={() => handleBlur("email")}
      />

      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={"password"}
        id="password"
        autoComplete="new-password"
        value={values.password}
        onChange={handleChange}
        error={!!errors.password}
        helperText={errors.password}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="confirmpassword"
        label="confirm password"
        type={showPassword ? "text" : "password"}
        id="confirmpassword"
        autoComplete="new-password"
        value={values.confirmpassword}
        onChange={handleChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={!!errors.confirmpassword}
        helperText={errors.confirmpassword}
      />

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                if (e.target.checked) setTermsError(false);
              }}
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I agree to the{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => window.open(ROUTES.PUBLIC.TERMS, '_blank')}
                sx={{ fontWeight: 600 }}
              >
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => window.open(ROUTES.PUBLIC.PRIVACY, '_blank')}
                sx={{ fontWeight: 600 }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        {termsError && (
          <FormHelperText error sx={{ ml: 4 }}>
            You must agree to the Terms &amp; Conditions and Privacy Policy to sign up.
          </FormHelperText>
        )}
      </Box>

      <LoadingButton
        fullWidth
        variant="contained"
        type="submit"
        loading={isLoading}
        disabled={Object.values(errors).some((err) => !!err)}
        sx={{
          mt: 2,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          "&:hover": {
        background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
          },
          "&.Mui-disabled": {
        background: "linear-gradient(135deg, #cfd8dc 0%, #b0bec5 100%)",
        color: "#757575",
          },
        }}
      >
        Sign Up
      </LoadingButton>

      <Box sx={{ textAlign: "center" }}>
        <Link href="/login" variant="body2">
          Already have an account? Sign In
        </Link>
      </Box>
    </Box>
  );
};

export default SignupForm;
